import { jsx } from "react/jsx-runtime";
import { c } from "react/compiler-runtime";
import { useCache } from "@effektify/cache";
import { useState, useEffect } from "react";
import DataContext from "./DataContext.js";
import useLogger from "./hooks/useLogger.js";
import useGetConfig from "./hooks/useGetConfig.js";
import useGetReportData from "./hooks/useGetReportData.js";
import useGetSubsequentData from "./hooks/useGetSubsequentData.js";
import { isConfig, isConfiguration } from "./types/src/guards/config.js";
import { isFieldsConfig, isDateRange } from "./types/src/guards/fields.js";
const hasEndpoint = (endpoint = []) => endpoint[0] && endpoint[0].uri && endpoint[0].method;
const getDefaultEndpoint = (endpoints = []) => {
  const defaultEndpoint = endpoints.find(endpoint => endpoint.auto);
  if (defaultEndpoint) {
    return defaultEndpoint;
  }
  return endpoints[0];
};
const sameParams = (params1, params2) => {
  if (Object.keys(params1).length !== Object.keys(params2).length) {
    return false;
  }
  return Object.keys(params1).every(key => {
    if (params1[key] !== params2[key]) {
      return false;
    }
    return true;
  });
};
const hasMissingRequired = (fields, currentParams) => Object.entries(fields).some(([key, field]) => field.required ? isDateRange(field) ? Object.values(field.props).some(reqKey => !(reqKey.name in currentParams)) : !currentParams[key] : false);
const getParamsFromFields = (fields, existingParams) => {
  const params = {
    ...existingParams
  };
  Object.entries(fields).forEach(([key, field]) => {
    if (isDateRange(field)) {
      const {
        start,
        end
      } = field.props;
      params[start.name] = start.default ? new Date(start.default).toISOString().split("T")[0] : "";
      params[end.name] = end.default ? new Date(end.default).toISOString().split("T")[0] : "";
      return;
    }
    if (field && field.default) {
      params[key] = field.default;
      return;
    }
    return;
  });
  return params;
};
const DataProvider = t0 => {
  const $ = c(52);
  const {
    config: apiConfig,
    children
  } = t0;
  const logger = useLogger("DataProvider");
  const [currentConfig, setCurrentConfig] = useState(null);
  const [currentParams, setCurrentParams] = useState(null);
  const [currentEndpoint, setCurrentEndpoint] = useState(null);
  const [shouldFetch, setShouldFetch] = useState(true);
  const [additionalEndpoints, setAdditionalEndpoints] = useState(null);
  const {
    resetDataOnly
  } = useCache();
  const {
    getToken,
    configURI
  } = apiConfig;
  let t1;
  if ($[0] !== configURI || $[1] !== getToken) {
    t1 = {
      getToken,
      configURI
    };
    $[0] = configURI;
    $[1] = getToken;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: reportConfig,
    isLoading: isConfigLoading
  } = useGetConfig(t1);
  const t2 = currentEndpoint ?? null;
  let t3;
  if ($[3] !== currentParams || $[4] !== getToken || $[5] !== shouldFetch || $[6] !== t2) {
    t3 = {
      getToken,
      endpoint: t2,
      params: currentParams,
      shouldFetch
    };
    $[3] = currentParams;
    $[4] = getToken;
    $[5] = shouldFetch;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const {
    data: t4,
    isLoading: isReportDataLoading,
    isPending: isReportDataPending,
    isFetching: isReportDataFetching
  } = useGetReportData(t3);
  const {
    dataEndpoints
  } = t4;
  const {
    isPending: isSubsequentPending,
    isWaiting: isSubsequentWaiting,
    completedFetches,
    settledMap: settledEndpoints
  } = useGetSubsequentData(additionalEndpoints, getToken);
  const isReportDataSettled = !isReportDataLoading && !isReportDataPending && !isReportDataFetching && dataEndpoints !== null;
  let t5;
  if ($[8] !== dataEndpoints) {
    t5 = !!dataEndpoints && Object.keys(dataEndpoints).length > 0;
    $[8] = dataEndpoints;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const hasSubsequentEndpoints = t5;
  const isSubsequentDataPending = hasSubsequentEndpoints && settledEndpoints !== dataEndpoints;
  const isDataSettled = isReportDataSettled && !isSubsequentDataPending;
  let t6;
  let t7;
  if ($[10] !== dataEndpoints || $[11] !== isReportDataLoading || $[12] !== isReportDataPending || $[13] !== logger) {
    t6 = () => {
      if (isReportDataPending || isReportDataLoading) {
        return;
      }
      if (!isReportDataPending && !isReportDataLoading && (!dataEndpoints || Object.keys(dataEndpoints).length === 0)) {
        return;
      }
      logger.debug("useEffect[dataEndpoints] Storing endpoints", dataEndpoints);
      setAdditionalEndpoints(dataEndpoints);
    };
    t7 = [dataEndpoints, isReportDataPending, isReportDataLoading, logger];
    $[10] = dataEndpoints;
    $[11] = isReportDataLoading;
    $[12] = isReportDataPending;
    $[13] = logger;
    $[14] = t6;
    $[15] = t7;
  } else {
    t6 = $[14];
    t7 = $[15];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[16] !== logger || $[17] !== reportConfig) {
    t8 = () => {
      if (isConfig(reportConfig)) {
        logger.debug("useEffect[reportConfig] Setting config", reportConfig);
        setCurrentConfig(prev => {
          if (JSON.stringify(prev) === JSON.stringify(reportConfig)) {
            return prev;
          }
          return reportConfig;
        });
      }
    };
    t9 = [reportConfig, logger, setCurrentConfig];
    $[16] = logger;
    $[17] = reportConfig;
    $[18] = t8;
    $[19] = t9;
  } else {
    t8 = $[18];
    t9 = $[19];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[20] !== currentConfig || $[21] !== logger) {
    t10 = () => {
      if (isConfig(currentConfig)) {
        logger.debug("useEffect[currentConfig] checking config for endpoints");
        const {
          configuration
        } = currentConfig;
        if (isConfiguration(configuration) && hasEndpoint(configuration.report_endpoints)) {
          const defaultEndpoint = getDefaultEndpoint(configuration.report_endpoints);
          logger.debug("useEffect[currentConfig] Storing default endpoint", defaultEndpoint);
          setCurrentEndpoint(prev_0 => {
            if (prev_0 && prev_0.uri === defaultEndpoint?.uri) {
              logger.debug("useEffect[currentConfig] Storing previous endpoint", prev_0);
              return prev_0;
            }
            return defaultEndpoint;
          });
        }
      }
    };
    t11 = [currentConfig, logger];
    $[20] = currentConfig;
    $[21] = logger;
    $[22] = t10;
    $[23] = t11;
  } else {
    t10 = $[22];
    t11 = $[23];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[24] !== currentConfig || $[25] !== currentParams || $[26] !== logger) {
    t12 = () => {
      logger.debug("useEffect[state.primaryEndpoint] should fetch from Main endpoint");
      if (!currentConfig?.fields) {
        return;
      }
      if (isFieldsConfig(currentConfig?.fields) && !currentParams) {
        const fields = currentConfig.fields;
        const defaultParams = getParamsFromFields(fields, currentParams);
        setCurrentParams(prev_1 => {
          logger.debug("useEffect[currentConfig] Storing params", prev_1);
          if (prev_1 && sameParams(prev_1, defaultParams)) {
            return prev_1;
          }
          return defaultParams;
        });
        const hasMissing = hasMissingRequired(fields, defaultParams);
        setShouldFetch(!hasMissing);
      }
    };
    t13 = [currentConfig, currentParams, logger, setCurrentParams, setShouldFetch];
    $[24] = currentConfig;
    $[25] = currentParams;
    $[26] = logger;
    $[27] = t12;
    $[28] = t13;
  } else {
    t12 = $[27];
    t13 = $[28];
  }
  useEffect(t12, t13);
  let t14;
  if ($[29] !== currentConfig || $[30] !== logger || $[31] !== resetDataOnly) {
    t14 = newParams => {
      logger.debug("handleSetParams", newParams);
      if (isConfig(currentConfig)) {
        if (!currentConfig.fields) {
          setCurrentParams(newParams);
        } else {
          const hasMissing_0 = hasMissingRequired(currentConfig.fields, newParams);
          if (!hasMissing_0) {
            logger.debug("handleSetParams: No missing required fields, invalidating data only");
            resetDataOnly();
          }
          setCurrentParams(newParams);
          setShouldFetch(!hasMissing_0);
        }
      }
    };
    $[29] = currentConfig;
    $[30] = logger;
    $[31] = resetDataOnly;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const handleSetParams = t14;
  const t15 = `[DataProvider]:${currentConfig?.configuration?.name || currentConfig?.configuration?.id || "default"}`;
  const t16 = currentConfig?.configuration || null;
  const t17 = currentConfig?.report || null;
  const t18 = currentConfig?.fields || null;
  const t19 = currentParams || null;
  const t20 = currentConfig?.cmsTags || null;
  const t21 = currentConfig?.configuration?.help?.key || null;
  const t22 = isReportDataLoading || isReportDataPending;
  const t23 = isSubsequentPending || isSubsequentWaiting;
  const t24 = currentParams?.no_comparison || false;
  let t25;
  if ($[33] !== completedFetches || $[34] !== handleSetParams || $[35] !== isConfigLoading || $[36] !== isDataSettled || $[37] !== isSubsequentDataPending || $[38] !== t15 || $[39] !== t16 || $[40] !== t17 || $[41] !== t18 || $[42] !== t19 || $[43] !== t20 || $[44] !== t21 || $[45] !== t22 || $[46] !== t23 || $[47] !== t24) {
    t25 = {
      id: t15,
      configuration: t16,
      report: t17,
      form: t18,
      formData: t19,
      cmsTags: t20,
      configurationHelp: t21,
      isConfigLoading,
      isLoading: t22,
      isSubsequentLoading: t23,
      isSubsequentDataPending,
      isDataSettled,
      noComparison: t24,
      completedFetches,
      handleSetParams
    };
    $[33] = completedFetches;
    $[34] = handleSetParams;
    $[35] = isConfigLoading;
    $[36] = isDataSettled;
    $[37] = isSubsequentDataPending;
    $[38] = t15;
    $[39] = t16;
    $[40] = t17;
    $[41] = t18;
    $[42] = t19;
    $[43] = t20;
    $[44] = t21;
    $[45] = t22;
    $[46] = t23;
    $[47] = t24;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  const contextValue = t25;
  let t26;
  if ($[49] !== children || $[50] !== contextValue) {
    t26 = /* @__PURE__ */jsx(DataContext.Provider, {
      value: contextValue,
      children
    });
    $[49] = children;
    $[50] = contextValue;
    $[51] = t26;
  } else {
    t26 = $[51];
  }
  return t26;
};
const Provider = t0 => {
  const $ = c(6);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== children || $[4] !== props) {
    t1 = /* @__PURE__ */jsx(DataProvider, {
      ...props,
      children
    });
    $[3] = children;
    $[4] = props;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
export { DataProvider, Provider as default };